import type { LegalSection } from '../types/content';

export const legalReviewNote =
  'This document is a working template prepared for Aurevane Search Partners. Bracketed provisions require review by qualified legal counsel for the relevant jurisdiction before publication.';

export const privacySections: LegalSection[] = [
  {
    heading: 'Who we are',
    paragraphs: [
      'This Privacy Policy explains how Aurevane Search Partners (“we”, “us”) collects and uses personal information through this website and in the course of executive search activities. [Insert registered legal entity name and registered address once confirmed.]',
    ],
  },
  {
    heading: 'Information we collect',
    paragraphs: [
      'When you submit a form on this website, we collect the information you provide — such as your name, email address, organization, job title, professional background, and, if you choose to upload one, your CV or résumé.',
      'We do not use analytics, advertising, or tracking tools on this website at present. If this changes, this policy and our Cookie Policy will be updated beforehand.',
    ],
  },
  {
    heading: 'How we use your information',
    paragraphs: [
      'For employers: to respond to hiring inquiries and discuss potential search assignments.',
      'For candidates: to consider your background for relevant current or future search assignments and to contact you about them. We will not share your details with a client organization without first discussing the opportunity with you.',
    ],
  },
  {
    heading: 'Legal basis for processing',
    paragraphs: ['[Specify the lawful bases relied upon — for example consent or legitimate interests — in line with the applicable data protection law.]'],
  },
  {
    heading: 'How long we keep information',
    paragraphs: ['[Insert retention periods for employer inquiries, candidate records, and CVs, and the process for review and deletion.]'],
  },
  {
    heading: 'How we protect information',
    paragraphs: [
      'Candidate details and CVs are never published on this website. Submitted information is intended to be stored in access-restricted systems available only to authorized members of our team. [Describe the specific storage provider and safeguards once configured.]',
    ],
  },
  {
    heading: 'Your rights',
    paragraphs: [
      'Depending on where you live, you may have rights to access, correct, delete, or restrict the use of your personal information, and to withdraw consent. To make a request, email any of our consultants listed on the Contact page. [Confirm applicable rights and the relevant supervisory authority.]',
    ],
  },
  {
    heading: 'Changes to this policy',
    paragraphs: ['We may update this policy from time to time. The latest version will always be available on this page.'],
  },
];

export const termsSections: LegalSection[] = [
  {
    heading: 'Use of this website',
    paragraphs: [
      'By using this website, you agree to these Terms of Use. The website provides general information about Aurevane Search Partners and its executive search services.',
    ],
  },
  {
    heading: 'No offer of employment',
    paragraphs: [
      'Submitting an inquiry or introducing yourself through this website does not create an employment relationship, guarantee consideration for a specific role, or constitute an offer of services. Search engagements with organizations are governed by separate written agreements.',
    ],
  },
  {
    heading: 'Content and intellectual property',
    paragraphs: [
      'The content, design, and branding on this website belong to Aurevane Search Partners unless otherwise stated, and may not be reproduced without permission. Articles marked as sample editorial content are illustrative only.',
    ],
  },
  {
    heading: 'Accuracy of information',
    paragraphs: ['We aim to keep information accurate and current, but the website content is provided for general information and does not constitute professional advice.'],
  },
  {
    heading: 'Limitation of liability',
    paragraphs: ['[Insert limitation of liability provisions appropriate to the governing jurisdiction.]'],
  },
  {
    heading: 'Governing law',
    paragraphs: ['[Insert governing law and jurisdiction once confirmed.]'],
  },
];

export const accessibilitySections: LegalSection[] = [
  {
    heading: 'Our commitment',
    paragraphs: [
      'We want this website to be usable by as many people as possible, including people who use assistive technologies. We have designed it with reference to the Web Content Accessibility Guidelines (WCAG) 2.2 at level AA. We have not yet completed a formal third-party audit and do not claim certified conformance.',
    ],
  },
  {
    heading: 'Measures we have taken',
    paragraphs: [
      'Semantic page structure and headings; keyboard-accessible navigation, menus, and forms; visible focus indicators; colour contrast chosen to meet AA ratios for body text; descriptive labels and error messages on every form field; and reduced-motion support that minimises animation, including the 3D hero, when your device requests it.',
    ],
  },
  {
    heading: 'Known limitations',
    paragraphs: [
      'Decorative 3D visuals are hidden from assistive technologies and are not required to use the site. If you encounter content that is difficult to access, please tell us.',
    ],
  },
  {
    heading: 'Feedback',
    paragraphs: ['If you have difficulty using any part of this website, email any of our consultants listed on the Contact page and we will do our best to help and to provide information in an alternative format.'],
  },
];

export const cookieSections: LegalSection[] = [
  {
    heading: 'What we use',
    paragraphs: [
      'This website does not currently set advertising, analytics, or third-party tracking cookies. We store a small preference in your browser’s local storage to remember the choices you make on this page.',
    ],
  },
  {
    heading: 'Third-party content',
    paragraphs: [
      'Fonts are loaded from Google Fonts, which may receive your IP address as part of delivering the font files. [Consider self-hosting fonts if required by applicable law.]',
    ],
  },
  {
    heading: 'Future changes',
    paragraphs: ['If analytics are introduced, they will be disabled by default until you opt in using the preferences below, and this policy will be updated.'],
  },
];
